import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Activity, AlertTriangle, MessageSquare, RefreshCw, Zap } from 'lucide-react';
import { Logo } from '@/components/Logo';
import { authController } from '@/states/authStateController';
import { useAuth } from '@chainlit/react-client';
import { BASE_URL, USAGE_ENDPOINT } from '@/lib/constants';

interface UsageHistoryItem {
  id: number;
  model: string;
  input_tokens: number;
  output_tokens: number;
  cost_usd: number;
  percent_of_allowance: number;
  created_at: string;
}

interface UsageData {
  user_id: number;
  allowance: number;
  used: number;
  remaining: number;
  percent_used: number;
  percent_remaining: number;
  is_warning: boolean;
  is_exhausted: boolean;
  period_start: string | null;
  period_end: string | null;
  history: UsageHistoryItem[];
}

const formatDate = (value: string | null) =>
  value
    ? new Date(value).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '—';

// Renders a whole date as "in N days" / "tomorrow" / "today" so the reset
// date is meaningful at a glance, without hiding the exact date.
const formatRelativeDays = (value: string | null) => {
  if (!value) return null;
  const diffMs = new Date(value).getTime() - Date.now();
  const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  if (days <= 0) return 'today';
  if (days === 1) return 'tomorrow';
  return `in ${days} days`;
};

// Sub-1% usage would otherwise round down to "0% used", which reads as "no
// usage recorded" even though credits have been spent.
const formatPercent = (percent: number) => {
  if (percent > 0 && percent < 1) return '<1';
  return percent.toFixed(0);
};

function UsageSkeleton() {
  return (
    <>
      <Card className="border-2 border-slate-200 shadow-xl bg-white/90 backdrop-blur-sm">
        <CardHeader className="border-b border-slate-100 bg-gradient-to-r from-blue-50 to-indigo-50">
          <Skeleton className="h-6 w-40 bg-slate-200/80" />
          <Skeleton className="h-4 w-56 bg-slate-200/60" />
        </CardHeader>
        <CardContent className="space-y-4 pt-6">
          <div className="space-y-3">
            <div className="flex justify-between items-end">
              <Skeleton className="h-9 w-28 bg-slate-200/80" />
              <Skeleton className="h-4 w-24 bg-slate-200/60" />
            </div>
            <Skeleton className="h-4 w-full rounded-full bg-slate-200/70" />
            <div className="flex justify-between">
              <Skeleton className="h-3 w-28 bg-slate-200/50" />
              <Skeleton className="h-3 w-28 bg-slate-200/50" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-2 border-slate-200 shadow-xl bg-white/90 backdrop-blur-sm">
        <CardHeader className="border-b border-slate-100 bg-gradient-to-r from-blue-50 to-indigo-50">
          <Skeleton className="h-6 w-36 bg-slate-200/80" />
          <Skeleton className="h-4 w-64 bg-slate-200/60" />
        </CardHeader>
        <CardContent className="pt-6 space-y-3">
          {[0, 1, 2].map(i => (
            <Skeleton key={i} className="h-10 w-full bg-slate-200/60" />
          ))}
        </CardContent>
      </Card>
    </>
  );
}

export default function Usage() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [usage, setUsage] = useState<UsageData | null>(null);
  const navigate = useNavigate();
  const { logout: chainlitLogout } = useAuth();

  const { user, isLoading: userLoading } = authController.useState(['user', 'isLoading']);

  const fetchUsage = useCallback(async () => {
    if (!user?.id) return;
    try {
      setIsLoading(true);
      setError('');
      const response = await fetch(`${BASE_URL}${USAGE_ENDPOINT}?user_id=${user.id}`, {
        method: 'GET',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
      });
      if (response.ok) {
        setUsage(await response.json());
      } else {
        const data = await response.json().catch(() => ({}));
        setError(data.detail || "We couldn't load your usage. Please try again.");
      }
    } catch (err) {
      console.error('Error fetching usage:', err);
      setError("We couldn't reach the server. Check your connection and try again.");
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    fetchUsage();
  }, [fetchUsage]);

  if (userLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-200 border-t-blue-600 mx-auto"></div>
          <p className="mt-4 text-slate-600 font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    navigate('/login');
    return null;
  }

  const percentUsed = usage ? Math.min(100, Math.max(0, usage.percent_used)) : 0;
  const percentRemaining = usage ? Math.max(0, usage.percent_remaining) : 100;
  const statusLabel = !usage
    ? null
    : usage.is_exhausted
    ? 'Limit reached'
    : usage.is_warning
    ? 'Running low'
    : 'On track';
  const statusClasses = usage?.is_exhausted
    ? 'bg-gradient-to-r from-red-100 to-rose-100 text-red-700 border-red-200'
    : usage?.is_warning
    ? 'bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-700 border-amber-200'
    : 'bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 border-green-200';
  const relativeReset = usage ? formatRelativeDays(usage.period_end) : null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 flex flex-col">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md border-b border-slate-200/50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-between items-center gap-y-2 py-4">
            <Logo className="h-8" />
            <div className="flex flex-wrap gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => navigate('/dashboard')}
                className="border-slate-300 bg-white/50 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 hover:border-blue-300 hover:shadow-md transition-all duration-200 text-slate-700 hover:text-blue-700 font-medium"
              >
                Dashboard
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate('/')}
                className="border-slate-300 bg-white/50 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 hover:border-blue-300 hover:shadow-md transition-all duration-200 text-slate-700 hover:text-blue-700 font-medium"
              >
                Go to App
              </Button>
              <Button
                variant="outline"
                onClick={() => chainlitLogout(true)}
                className="border-slate-300 bg-white/50 hover:bg-gradient-to-r hover:from-slate-50 hover:to-slate-100 hover:border-slate-400 hover:shadow-md transition-all duration-200 text-slate-700 hover:text-slate-900 font-medium"
              >
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto space-y-8">
          <div className="text-center">
            <h1 className="text-3xl font-extrabold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
              Usage
            </h1>
            <p className="mt-2 text-sm text-slate-600">
              Track your monthly AI usage and remaining credits
            </p>
          </div>

          {error && (
            <Alert variant="destructive" className="border-red-200 bg-red-50">
              <AlertTriangle className="h-4 w-4 text-red-600" aria-hidden="true" />
              <AlertDescription className="text-red-800">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <span>{error}</span>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={fetchUsage}
                    disabled={isLoading}
                    className="shrink-0 border-red-300 bg-white/70 text-red-700 hover:bg-red-100 hover:text-red-800"
                  >
                    <RefreshCw
                      className={`mr-1.5 h-3.5 w-3.5 ${isLoading ? 'animate-spin' : ''}`}
                    />
                    Try again
                  </Button>
                </div>
              </AlertDescription>
            </Alert>
          )}

          {!usage && isLoading && <UsageSkeleton />}

          {usage && (
            <>
              {/* Credit summary */}
              <Card className="border-2 border-slate-200 shadow-xl bg-white/90 backdrop-blur-sm">
                <CardHeader className="border-b border-slate-100 bg-gradient-to-r from-blue-50 to-indigo-50">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <CardTitle className="text-xl text-slate-900 flex items-center gap-2">
                      <Zap className="h-5 w-5 text-blue-600" aria-hidden="true" />
                      Monthly Credits
                    </CardTitle>
                    {statusLabel && (
                      <span
                        className={`text-xs px-2.5 py-1 rounded-full font-medium border ${statusClasses}`}
                      >
                        {statusLabel}
                      </span>
                    )}
                  </div>
                  <CardDescription className="text-slate-600">
                    {relativeReset
                      ? `Resets ${relativeReset} (${formatDate(usage.period_end)})`
                      : `Resets on ${formatDate(usage.period_end)}`}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6 pt-6">
                  {usage.is_exhausted && (
                    <Alert className="border-red-200 bg-red-50">
                      <AlertTriangle className="h-4 w-4 text-red-600" aria-hidden="true" />
                      <AlertDescription className="text-red-800">
                        You've used all of this month's AI credits. New chat messages are
                        paused until they reset{' '}
                        {relativeReset ?? 'soon'} ({formatDate(usage.period_end)}).
                      </AlertDescription>
                    </Alert>
                  )}
                  {!usage.is_exhausted && usage.is_warning && (
                    <Alert className="border-amber-200 bg-amber-50">
                      <AlertTriangle className="h-4 w-4 text-amber-600" aria-hidden="true" />
                      <AlertDescription className="text-amber-800">
                        You're approaching your monthly limit — about{' '}
                        {Math.round(percentRemaining)}% remains until it resets{' '}
                        {relativeReset ?? 'soon'}.
                      </AlertDescription>
                    </Alert>
                  )}

                  <div className="space-y-2" aria-live="polite">
                    <div className="flex justify-between items-end">
                      <span className="text-3xl font-extrabold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                        {formatPercent(percentUsed)}% used
                      </span>
                      <span className="text-sm font-medium text-slate-600">
                        {Math.round(percentRemaining)}% remaining
                      </span>
                    </div>
                    <Progress
                      value={percentUsed}
                      aria-label="Monthly AI credits used"
                      aria-valuetext={`${formatPercent(percentUsed)}% of monthly credits used`}
                      className={`transition-all duration-500 ${
                        usage.is_exhausted
                          ? '[&>div]:bg-red-500'
                          : usage.is_warning
                          ? '[&>div]:bg-amber-500'
                          : '[&>div]:bg-blue-600'
                      }`}
                    />
                    <div className="flex justify-between text-xs text-slate-500">
                      <span>Period started {formatDate(usage.period_start)}</span>
                      <span>Resets {formatDate(usage.period_end)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Usage history */}
              <Card className="border-2 border-slate-200 shadow-xl bg-white/90 backdrop-blur-sm">
                <CardHeader className="border-b border-slate-100 bg-gradient-to-r from-blue-50 to-indigo-50">
                  <CardTitle className="text-xl text-slate-900 flex items-center gap-2">
                    <Activity className="h-5 w-5 text-blue-600" aria-hidden="true" />
                    Usage History
                  </CardTitle>
                  <CardDescription className="text-slate-600">
                    Your most recent AI requests this period
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-6">
                  {usage.history.length === 0 ? (
                    <div className="text-center py-10 space-y-3">
                      <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
                        <MessageSquare
                          className="h-6 w-6 text-slate-400"
                          aria-hidden="true"
                        />
                      </div>
                      <p className="text-sm font-medium text-slate-700">
                        No usage yet this period
                      </p>
                      <p className="text-sm text-slate-500 max-w-sm mx-auto">
                        Start a conversation with your Sales Coach and your AI usage will
                        show up here.
                      </p>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate('/')}
                        className="mt-2 border-slate-300 bg-white/50 hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 hover:border-blue-300 text-slate-700 hover:text-blue-700 font-medium"
                      >
                        Go to App
                      </Button>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="whitespace-nowrap">Date</TableHead>
                          <TableHead className="whitespace-nowrap">Model</TableHead>
                          <TableHead className="text-right whitespace-nowrap">
                            Tokens (in / out)
                          </TableHead>
                          <TableHead className="text-right whitespace-nowrap">
                            % of monthly
                          </TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {usage.history.map(item => (
                          <TableRow key={item.id}>
                            <TableCell className="text-slate-700 whitespace-nowrap">
                              {formatDate(item.created_at)}
                            </TableCell>
                            <TableCell className="text-slate-700 whitespace-nowrap">
                              {item.model}
                            </TableCell>
                            <TableCell className="text-right font-mono text-slate-600 whitespace-nowrap">
                              {item.input_tokens.toLocaleString()} /{' '}
                              {item.output_tokens.toLocaleString()}
                            </TableCell>
                            <TableCell className="text-right font-medium text-slate-900 whitespace-nowrap">
                              {item.percent_of_allowance < 0.01 &&
                              item.percent_of_allowance > 0
                                ? '<0.01'
                                : item.percent_of_allowance.toFixed(2)}
                              %
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
